// W35.1 (2026-10-20): the History tab's third snapshot card on a real
// session. The approved design (launch-ready/approved-stack-2026-10-12/
// 10-History-Consistency-Review-v5.html line 376-379) renders it as
//
//   <article class="history-snapshot-card subscription">
//     <div><small>NEXT SUBSCRIPTION</small>
//          <strong>Oct 15 · Barn supplements</strong>
//          <p>2 products · scheduled · no payment issue</p></div>
//
// i.e. label / "date · product" headline / detail line. This component
// uses the same snapshot-card markup as its three siblings (W40 moved all
// four onto the approved icon / small / strong / p structure) and fills
// it from the W35 subscription summary instead of a timeline event. A
// summary on file opens the Subscriptions tab; the empty, loading and
// error states stay inert like the other honest empties.

import type { ReactNode } from 'react';
import { Icon } from '../_shared/IconSprite.js';
import {
  describeSubscriptionCardError,
  SUBSCRIPTION_CARD_ERROR_TITLE,
  SUBSCRIPTION_CARD_LOADING,
  SUBSCRIPTION_CARD_NONE,
  type SubscriptionCardState,
} from '../_shared/subscriptionCard.js';

export interface NextSubscriptionCardProps {
  /** The section label; 'NEXT SUBSCRIPTION' from HISTORY_SECTIONS. */
  label: string;
  state: Exclude<SubscriptionCardState, { kind: 'skipped' }>;
  /** Opens the Subscriptions tab for this contact. */
  onOpen: () => void;
}

export function nextSubscriptionCardCopy(state: NextSubscriptionCardProps['state']): { title: string; note: string } {
  switch (state.kind) {
    case 'loading':
      return { title: SUBSCRIPTION_CARD_LOADING, note: 'Reading HubSpot contact properties' };
    case 'error':
      return { title: SUBSCRIPTION_CARD_ERROR_TITLE, note: `Error code: ${describeSubscriptionCardError(state)}` };
    case 'none':
      return { title: SUBSCRIPTION_CARD_NONE, note: 'No Recharge subscription status on the HubSpot contact' };
    case 'on-file':
      return {
        title: `${state.facts.nextCharge} · ${state.facts.product}`,
        note: `${state.facts.cadence} · ${state.facts.payment} · ${state.facts.status}`,
      };
  }
}

export function NextSubscriptionCard({ label, state, onOpen }: NextSubscriptionCardProps): ReactNode {
  const onFile = state.kind === 'on-file';
  const { title, note } = nextSubscriptionCardCopy(state);
  return (
    <button
      className="card history-snapshot-card subscription"
      type="button"
      disabled={!onFile}
      aria-busy={state.kind === 'loading' ? true : undefined}
      data-testid="history-section-subscription"
      data-subscription-state={state.kind}
      onClick={() => { if (onFile) onOpen(); }}
    >
      <span className="snapshot-icon"><Icon id="clock" /></span>
      <div>
        <small>{label}</small>
        <strong>{title}</strong>
        <p>{note}</p>
      </div>
    </button>
  );
}
